/* eslint-disable import/no-anonymous-default-export */
import { Action } from "../../../domain/entities/ActionModel";
import Campaign from "../../../domain/entities/Campaign";
import DocumentTemplate from "../../../domain/entities/DocumentTemplate";
import Message from "../../../domain/entities/Message";
import Package from "../../../domain/entities/Package";
import SectionsData from "../../../domain/entities/Section";
import SelectRouting from "../../../domain/entities/SelectRouting";
import SignatoryGroup from "../../../domain/entities/SignatoryGroup";
import { RequestorState, StateStatus } from "../../../domain/entities/State";
import StudentPostgis, { PostgisAssignment, PostgisProgram, PostgisSchool } from "../../../domain/entities/StudentPostgis";
import { ActionTypes } from "../actions/requestor";

const initialState: RequestorState = {
  campaign: null,
  campaignLoadStatus: StateStatus.INIT,
  campaignLoadError: null,
  campaignSaveStatus: StateStatus.INIT,
  campaignSaveError: null,
  campaignSendStatus: StateStatus.INIT,
  campaignSendError: null,
  campaignSaveSendStatus: StateStatus.INIT,
  campaignSaveSendError: null,
  campaignsLoadStatus: StateStatus.INIT,
  campaigns: [] as Campaign[],
  campaignsLoadError: null,
  campaignDeleteStatus: StateStatus.INIT,
  campaignDeleteError: null,
  campaignsCountLoadStatus: StateStatus.INIT,
  campaignsCount: null,
  campaignsCountLoadError: null,
  documentStaffProcessedLoadStatus: StateStatus.INIT,
  documentStaffProcessed: null,
  documentStaffProcessedLoadError: null,
  documentTemplate: null,
  documentTemplateLoadStatus: StateStatus.INIT,
  documentTemplateLoadError: null,
  documentTemplateSaveStatus: StateStatus.INIT,
  documentTemplateSaveError: null,
  documentTemplateDeleteStatus: StateStatus.INIT,
  documentTemplateDeleteError: null,
  documentTemplateSignaturePositionStatus: StateStatus.INIT,
  documentTemplateSignaturePositionError: null,
  documentTemplatesLoadStatus: StateStatus.INIT,
  documentTemplates: [] as DocumentTemplate[],
  documentTemplatesLoadError: null,
  packagesLoadStatus: StateStatus.INIT,
  packages: [] as Package[],
  packagesLoadError: null,
  packageSaveStatus: StateStatus.INIT,
  packageSaveError: null,
  packageDeleteStatus: StateStatus.INIT,
  packageDeleteError: null,
  selectRoutingsLoadStatus: StateStatus.INIT,
  selectRoutings: [] as SelectRouting[],
  selectRoutingsLoadError: null,
  selectRoutingSaveStatus: StateStatus.INIT,
  selectRoutingSaveError: null,
  selectRoutingDeleteStatus: StateStatus.INIT,
  selectRoutingDeleteError: null,
  signatoryGroupLoadStatus: StateStatus.INIT,
  signatoryGroups: [] as SignatoryGroup[],
  signatoryGroupLoadError: null,
  signatoryGroupSaveStatus: StateStatus.INIT,
  signatoryGroupSaveError: null,
  signatoryGroupDeleteStatus: StateStatus.INIT,
  signatoryGroupDeleteError: null,
  messageLoadStatus: StateStatus.INIT,
  message: null,
  messageLoadError: null,
  messageSaveStatus: StateStatus.INIT,
  messageSaveError: null,
  messagesLoadStatus: StateStatus.INIT,
  messages: [] as Message[],
  messagesLoadError: null,
  messageDeleteStatus: StateStatus.INIT,
  messageDeleteError: null,
  messagesCountLoadStatus: StateStatus.INIT,
  messagesCount: null,
  messagesCountLoadError: null,
  messageSendStatus: StateStatus.INIT,
  messageSendError: null,
  externalSchoolIntegrationSections: [] as SectionsData[],
  externalSchoolIntegrationSectionsLoadError: null,
  externalSchoolIntegrationSectionsLoadStatus: StateStatus.INIT,
  externalSchoolIntegrationGrades: [],
  externalSchoolIntegrationGradesLoadError: null,
  externalSchoolIntegrationGradesLoadStatus: StateStatus.INIT,
  externalSchoolIntegrationStudents: [],
  externalSchoolIntegrationStudentsLoadError: null,
  externalSchoolIntegrationStudentsLoadStatus: StateStatus.INIT,
  externalSchoolIntegrationStudentSignatories: [],
  externalSchoolIntegrationStudentSignatoriesError: null,
  externalSchoolIntegrationStudentSignatoriesLoadStatus: StateStatus.INIT,
  externalSchoolIntegrationSignatories: [],
  externalSchoolIntegrationSignatoriesError: null,
  externalSchoolIntegrationSignatoriesLoadStatus: StateStatus.INIT,
  signingDocuments: [],
  signingDocumentsLoadError: null,
  signingDocumentsLoadStatus: StateStatus.INIT,
  emailTracking: {},
  emailTrackingStats: {},
  emailTrackingLoadError: null,
  emailTrackingLoadStatus: StateStatus.INIT,
  postgisSchoolsLoadStatus: StateStatus.INIT,
  postgisSchools: [] as PostgisSchool[],
  postgisSchoolsLoadError: null,
  postgisGradesLoadStatus: StateStatus.INIT,
  postgisGrades: [] as string[],
  postgisGradesLoadError: null,
  postgisProgramsLoadStatus: StateStatus.INIT,
  postgisPrograms: [] as PostgisProgram[],
  postgisProgramsLoadError: null,
  postgisAssignmentsLoadStatus: StateStatus.INIT,
  postgisAssignments: [] as PostgisAssignment[],
  postgisAssignmentsLoadError: null,
  postgisStudentsLoadStatus: StateStatus.INIT,
  postgisStudents: [] as StudentPostgis[],
  postgisStudentsLoadError: null,
  postgisStudentsExcludedCount: 0,
  studentsForGradesAndProgramsLoadStatus: StateStatus.INIT,
  studentsForGradesAndPrograms: {},
  studentsForGradesAndProgramsLoadError: null,
  studentsBySchoolLoadStatus: StateStatus.INIT,
  studentsBySchool: {},
  studentsBySchoolLoadError: null,
  programDemandLoadStatus: StateStatus.INIT,
  programDemand: null,
  programDemandLoadError: null,
  choiceDashboardLoadStatus: StateStatus.INIT,
  choiceDashboard: null,
  choiceDashboardLoadError: null,
  choiceDashboardRequestId: 0,
  projectedEnrollmentLoadStatus: StateStatus.INIT,
  projectedEnrollment: {},
  projectedEnrollmentLoadError: null,
  lastEvaluatedKey: undefined,
};

export default (state = initialState, action: Action<any>): RequestorState => {
  switch (action.type) {
    case ActionTypes.CAMPAIGN_LOAD_COMPLETE: {
      return {
        ...state,
        campaignLoadStatus: StateStatus.COMPLETE,
        campaign: action.payload,
        campaignLoadError: null,
      };
    }
    case ActionTypes.CAMPAIGN_LOAD_ERROR: {
      return {
        ...state,
        campaignLoadStatus: StateStatus.ERROR,
        campaignLoadError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGN_LOAD_PROCESSING: {
      return {
        ...state,
        campaignLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGNS_RESTORE_STATES: {
      return {
        ...state,
        campaignSaveStatus: StateStatus.INIT,
        campaignSendStatus: StateStatus.INIT,
        campaignsLoadStatus: StateStatus.INIT,
        campaignsCountLoadStatus: StateStatus.INIT,
      };
    }
    case ActionTypes.CAMPAIGNS_CLEAN_VALUE: {
      return {
        ...state,
        campaigns: [],
      };
    }
    case ActionTypes.CAMPAIGN_CLEAN_VALUE: {
      return {
        ...state,
        campaign: null,
      };
    }
    case ActionTypes.CAMPAIGNS_LOAD_COMPLETE: {
      return {
        ...state,
        campaignsLoadStatus: StateStatus.COMPLETE,
        campaigns: action.payload,
        campaignsLoadError: null,
      };
    }
    case ActionTypes.CAMPAIGNS_LOAD_ERROR: {
      return {
        ...state,
        campaignsLoadStatus: StateStatus.ERROR,
        campaignsLoadError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGNS_LOAD_PROCESSING: {
      return {
        ...state,
        campaignsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGNS_PAGINATION_LOAD_COMPLETE: {
      const existingIds = new Set(state.campaigns.map((c: any) => c.id));
      const newItems = action.payload.items.filter((c: any) => !existingIds.has(c.id));
      return {
        ...state,
        campaignsLoadStatus: StateStatus.COMPLETE,
        campaigns: [...state.campaigns, ...newItems],
        lastEvaluatedKey: action.payload.lastEvaluatedKey  || null,
        campaignsLoadError: null,
      };
    }    
    case ActionTypes.CAMPAIGNS_PAGINATION_LOAD_ERROR: {
      return {
        ...state,
        campaignsLoadStatus: StateStatus.ERROR,
        campaignsLoadError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGNS_PAGINATION_LOAD_PROCESSING: {
      return {
        ...state,
        campaignsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.LASTVALUEKEY_CLEAN: {
      return {
        ...state,
        lastEvaluatedKey: undefined,
      };
    }
    case ActionTypes.CAMPAIGNS_COUNT_CLEAN: {
      return {
        ...state,
        campaignsCount: null,
      };
    }
    case ActionTypes.CAMPAIGNS_COUNT_LOAD_COMPLETE: {
      return {
        ...state,
        campaignsCountLoadStatus: StateStatus.COMPLETE,
        campaignsCount: action.payload,
        campaignsCountLoadError: null,
      };
    }    
    case ActionTypes.CAMPAIGNS_COUNT_LOAD_ERROR: {
      return {
        ...state,
        campaignsCountLoadStatus: StateStatus.ERROR,
        campaignsCountLoadError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGNS_COUNT_LOAD_PROCESSING: {
      return {
        ...state,
        campaignsCountLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_COMPLETE: {
      const { package: campaignPackage, signatoryGroup } = action.payload;
      return {
        ...state,
        campaignSaveStatus: StateStatus.COMPLETE,
        campaignSaveError: null,
        campaigns: state.campaigns.some((camp) => camp.id === action.payload.id)
          ? state.campaigns
          : [...state.campaigns, action.payload],
        packages:
          campaignPackage &&
          !state.packages.some((p) => p.id === campaignPackage.id)
            ? [...state.packages, campaignPackage]
            : state.packages,
        documentTemplates: campaignPackage?.documentTemplates?.filter(
          (newItem: any) =>
            !state.documentTemplates.some(
              (existingItem: any) => existingItem.id === newItem.id
            )
        ).length
          ? [...state.documentTemplates, ...campaignPackage.documentTemplates]
          : state.documentTemplates,
        signatoryGroups:
          signatoryGroup &&
          !state.signatoryGroups.some((group) => group.id === signatoryGroup.id)
            ? [...state.signatoryGroups, signatoryGroup]
            : state.signatoryGroups,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_ERROR: {
      return {
        ...state,
        campaignSaveStatus: StateStatus.ERROR,
        campaignSaveError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_PROCESSING: {
      return {
        ...state,
        campaignSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_DELETE_COMPLETE: {
      return {
        ...state,
        campaignDeleteStatus: StateStatus.COMPLETE,
        campaignDeleteError: null,
      };
    }
    case ActionTypes.CAMPAIGN_DELETE_ERROR: {
      return {
        ...state,
        campaignDeleteStatus: StateStatus.ERROR,
        campaignDeleteError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGN_DELETE_PROCESSING: {
      return {
        ...state,
        campaignDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_LOCAL_DELETE: {
      return {
        ...state,
        campaigns: state.campaigns.filter(
          (campaign) => campaign.id !== action.payload
        ),
      };
    }
    case ActionTypes.CAMPAIGN_SEND_COMPLETE: {
      let campaignData: Campaign[] = Array.isArray(state.campaigns)
        ? state.campaigns
        : [];

      campaignData = campaignData.map((camp) => {
        if (camp.id === action.payload) {
          return { ...camp, status: "started" };
        } else {
          return camp;
        }
      });

      return {
        ...state,
        campaignSendStatus: StateStatus.COMPLETE,
        campaignSendError: null,
        campaigns: campaignData,
      };
    }
    case ActionTypes.CAMPAIGN_SEND_ERROR: {
      return {
        ...state,
        campaignSendStatus: StateStatus.ERROR,
        campaignSendError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGN_SEND_PROCESSING: {
      return {
        ...state,
        campaignSendStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_SEND_COMPLETE: {
      return {
        ...state,
        campaignSaveSendStatus: StateStatus.COMPLETE,
        campaignSaveSendError: null,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_SEND_ERROR: {
      return {
        ...state,
        campaignSaveSendStatus: StateStatus.ERROR,
        campaignSaveSendError: action.payload,
      };
    }
    case ActionTypes.CAMPAIGN_SAVE_SEND_PROCESSING: {
      return {
        ...state,
        campaignSaveSendStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CLEAR_DOCUMENT_STAFF: {
      return {
        ...state,
        documentStaffProcessed: null,
      };
    }
    case ActionTypes.DOCUMENT_STAFF_PROCESS_PROCESSING: {
      return {
        ...state,
        documentStaffProcessedLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENT_STAFF_PROCESS_COMPLETE: {
      return {
        ...state,
        documentStaffProcessedLoadStatus: StateStatus.COMPLETE,
        documentStaffProcessed: action.payload,
        documentStaffProcessedLoadError: null,
      };
    }
    case ActionTypes.DOCUMENT_STAFF_PROCESS_ERROR: {
      return {
        ...state,
        documentStaffProcessedLoadStatus: StateStatus.ERROR,
        documentStaffProcessedLoadError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATES_LOAD_COMPLETE: {
      return {
        ...state,
        documentTemplatesLoadStatus: StateStatus.COMPLETE,
        documentTemplates: action.payload,
        documentTemplatesLoadError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATES_LOAD_ERROR: {
      return {
        ...state,
        documentTemplatesLoadStatus: StateStatus.ERROR,
        documentTemplatesLoadError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATES_LOAD_PROCESSING: {
      return {
        ...state,
        documentTemplatesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SAVE_COMPLETE: {
      return {
        ...state,
        documentTemplateSaveStatus: StateStatus.COMPLETE,
        documentTemplateSaveError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SAVE_ERROR: {
      return {
        ...state,
        documentTemplateSaveStatus: StateStatus.ERROR,
        documentTemplateSaveError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SAVE_PROCESSING: {
      return {
        ...state,
        documentTemplateSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SIGNATURE_POSITION_COMPLETE: {
      return {
        ...state,
        documentTemplateSignaturePositionStatus: StateStatus.COMPLETE,
        documentTemplate: action.payload,
        documentTemplateSignaturePositionError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SIGNATURE_POSITION_ERROR: {
      return {
        ...state,
        documentTemplateSignaturePositionStatus: StateStatus.ERROR,
        documentTemplateSignaturePositionError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_SIGNATURE_POSITION_PROCESSING: {
      return {
        ...state,
        documentTemplateSignaturePositionStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.PACKAGES_LOAD_COMPLETE: {
      return {
        ...state,
        packagesLoadStatus: StateStatus.COMPLETE,
        packages: action.payload,
        packagesLoadError: null,
      };
    }
    case ActionTypes.PACKAGES_LOAD_ERROR: {
      return {
        ...state,
        packagesLoadStatus: StateStatus.ERROR,
        packages: [],
        packagesLoadError: action.payload,
      };
    }
    case ActionTypes.PACKAGES_LOAD_PROCESSING: {
      return {
        ...state,
        packagesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_DELETE_COMPLETE: {
      return {
        ...state,
        documentTemplateDeleteStatus: StateStatus.COMPLETE,
        documentTemplateDeleteError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_DELETE_ERROR: {
      return {
        ...state,
        documentTemplateDeleteStatus: StateStatus.ERROR,
        documentTemplateDeleteError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_DELETE_PROCESSING: {
      return {
        ...state,
        documentTemplateDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.PACKAGE_SAVE_COMPLETE: {
      return {
        ...state,
        packageSaveStatus: StateStatus.COMPLETE,
        packageSaveError: null,
      };
    }
    case ActionTypes.PACKAGE_SAVE_ERROR: {
      return {
        ...state,
        packageSaveStatus: StateStatus.ERROR,
        packageSaveError: action.payload,
      };
    }
    case ActionTypes.PACKAGE_SAVE_PROCESSING: {
      return {
        ...state,
        packageSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.PACKAGE_DELETE_COMPLETE: {
      return {
        ...state,
        packageDeleteStatus: StateStatus.COMPLETE,
        packageDeleteError: null,
      };
    }
    case ActionTypes.PACKAGE_DELETE_ERROR: {
      return {
        ...state,
        packageDeleteStatus: StateStatus.ERROR,
        packageDeleteError: action.payload,
      };
    }
    case ActionTypes.PACKAGE_DELETE_PROCESSING: {
      return {
        ...state,
        packageDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SELECTROUTINGS_LOAD_COMPLETE: {
      return {
        ...state,
        selectRoutingsLoadStatus: StateStatus.COMPLETE,
        selectRoutings: action.payload,
        selectRoutingsLoadError: null,
      };
    }
    case ActionTypes.SELECTROUTINGS_LOAD_ERROR: {
      return {
        ...state,
        selectRoutingsLoadStatus: StateStatus.ERROR,
        selectRoutings: [],
        selectRoutingsLoadError: action.payload,
      };
    }
    case ActionTypes.SELECTROUTINGS_LOAD_PROCESSING: {
      return {
        ...state,
        selectRoutingsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SELECTROUTING_SAVE_COMPLETE: {
      return {
        ...state,
        selectRoutingSaveStatus: StateStatus.COMPLETE,
        selectRoutingSaveError: null,
      };
    }
    case ActionTypes.SELECTROUTING_SAVE_ERROR: {
      return {
        ...state,
        selectRoutingSaveStatus: StateStatus.ERROR,
        selectRoutingSaveError: action.payload,
      };
    }
    case ActionTypes.SELECTROUTING_SAVE_PROCESSING: {
      return {
        ...state,
        selectRoutingSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SELECTROUTING_DELETE_COMPLETE: {
      return {
        ...state,
        selectRoutingDeleteStatus: StateStatus.COMPLETE,
        selectRoutingDeleteError: null,
      };
    }
    case ActionTypes.SELECTROUTING_DELETE_ERROR: {
      return {
        ...state,
        selectRoutingDeleteStatus: StateStatus.ERROR,
        selectRoutingDeleteError: action.payload,
      };
    }
    case ActionTypes.SELECTROUTING_DELETE_PROCESSING: {
      return {
        ...state,
        selectRoutingDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SIGNATORYGROUPS_LOAD_COMPLETE: {
      return {
        ...state,
        signatoryGroupLoadStatus: StateStatus.COMPLETE,
        signatoryGroups: action.payload,
        signatoryGroupLoadError: null,
      };
    }
    case ActionTypes.SIGNATORYGROUPS_LOAD_ERROR: {
      return {
        ...state,
        signatoryGroupLoadStatus: StateStatus.ERROR,
        signatoryGroups: [],
        signatoryGroupLoadError: action.payload,
      };
    }
    case ActionTypes.SIGNATORYGROUPS_LOAD_PROCESSING: {
      return {
        ...state,
        signatoryGroupLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SIGNATORYGROUP_SAVE_COMPLETE: {
      return {
        ...state,
        signatoryGroupSaveStatus: StateStatus.COMPLETE,
        signatoryGroupSaveError: null,
      };
    }
    case ActionTypes.SIGNATORYGROUP_SAVE_ERROR: {
      return {
        ...state,
        signatoryGroupSaveStatus: StateStatus.ERROR,
        signatoryGroupSaveError: action.payload,
      };
    }
    case ActionTypes.SIGNATORYGROUP_SAVE_PROCESSING: {
      return {
        ...state,
        signatoryGroupSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.SIGNATORYGROUP_DELETE_COMPLETE: {
      return {
        ...state,
        signatoryGroupDeleteStatus: StateStatus.COMPLETE,
        signatoryGroupDeleteError: null,
      };
    }
    case ActionTypes.SIGNATORYGROUP_DELETE_ERROR: {
      return {
        ...state,
        signatoryGroupDeleteStatus: StateStatus.ERROR,
        signatoryGroupDeleteError: action.payload,
      };
    }
    case ActionTypes.SIGNATORYGROUP_DELETE_PROCESSING: {
      return {
        ...state,
        signatoryGroupDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_LOAD_COMPLETE: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.COMPLETE,
        documentTemplate: action.payload,
        documentTemplateLoadError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_LOAD_ERROR: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.ERROR,
        documentTemplate: null,
        documentTemplateLoadError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_LOAD_PROCESSING: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_BY_CAMPAIGN_LOAD_COMPLETE: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.COMPLETE,
        documentTemplate: action.payload,
        documentTemplateLoadError: null,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_BY_CAMPAIGN_LOAD_ERROR: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.ERROR,
        documentTemplate: null,
        documentTemplateLoadError: action.payload,
      };
    }
    case ActionTypes.DOCUMENTTEMPLATE_BY_CAMPAIGN_LOAD_PROCESSING: {
      return {
        ...state,
        documentTemplateLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MESSAGE_SAVE_COMPLETE: {
      const { signatoryGroup } = action.payload;
      return {
        ...state,
        messageSaveStatus: StateStatus.COMPLETE,
        messageSaveError: null,
        messages: state.messages.some((message) => message.id === action.payload.id)
          ? state.messages
          : [...state.messages, action.payload],
        signatoryGroups:
          signatoryGroup &&
          !state.signatoryGroups.some((group) => group.id === signatoryGroup.id)
            ? [...state.signatoryGroups, signatoryGroup]
            : state.signatoryGroups,
      };
    }
    case ActionTypes.MESSAGE_SAVE_ERROR: {
      return {
        ...state,
        messageSaveStatus: StateStatus.ERROR,
        messageSaveError: action.payload,
      };
    }
    case ActionTypes.MESSAGE_SAVE_PROCESSING: {
      return {
        ...state,
        messageSaveStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MESSAGE_LOAD_COMPLETE: {
      return {
        ...state,
        messageLoadStatus: StateStatus.COMPLETE,
        message: action.payload,
        messageLoadError: null,
      };
    }
    case ActionTypes.MESSAGE_LOAD_ERROR: {
      return {
        ...state,
        messageLoadStatus: StateStatus.ERROR,
        messageLoadError: action.payload,
      };
    }
    case ActionTypes.MESSAGE_LOAD_PROCESSING: {
      return {
        ...state,
        messageLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MESSAGES_RESTORE_STATES: {
      return {
        ...state,
        messageSaveStatus: StateStatus.INIT,
        messageSendStatus: StateStatus.INIT,
        messagesLoadStatus: StateStatus.INIT,
      };
    }
    case ActionTypes.MESSAGES_CLEAN_VALUE: {
      return {
        ...state,
        messages: [],
      };
    }
    case ActionTypes.MESSAGES_LOAD_COMPLETE: {
      return {
        ...state,
        messagesLoadStatus: StateStatus.COMPLETE,
        messages: action.payload,
        messagesLoadError: null,
      };
    }
    case ActionTypes.MESSAGES_LOAD_ERROR: {
      return {
        ...state,
        messagesLoadStatus: StateStatus.ERROR,
        messagesLoadError: action.payload,
      };
    }
    case ActionTypes.MESSAGES_LOAD_PROCESSING: {
      return {
        ...state,
        messagesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MESSAGE_DELETE_COMPLETE: {
      return {
        ...state,
        messageDeleteStatus: StateStatus.COMPLETE,
        messageDeleteError: null,
      };
    }
    case ActionTypes.MESSAGE_DELETE_ERROR: {
      return {
        ...state,
        messageDeleteStatus: StateStatus.ERROR,
        messageDeleteError: action.payload,
      };
    }
    case ActionTypes.MESSAGE_DELETE_PROCESSING: {
      return {
        ...state,
        messageDeleteStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.MESSAGE_LOCAL_DELETE: {
      return {
        ...state,
        messages: state.messages.filter(
          (message) => message.id !== action.payload
        ),
      };
    }
    case ActionTypes.MESSAGES_PAGINATION_LOAD_COMPLETE: {
      const existingMsgIds = new Set(state.messages.map((m: any) => m.id));
      const newMsgs = action.payload.items.filter((m: any) => !existingMsgIds.has(m.id));
      return {
        ...state,
        messagesLoadStatus: StateStatus.COMPLETE,
        messages: [...state.messages, ...newMsgs],
        lastEvaluatedKey: action.payload.lastEvaluatedKey || null,
        messagesLoadError: null,
      };
    }
    case ActionTypes.MESSAGES_PAGINATION_LOAD_ERROR: {
      return {
        ...state,
        messagesLoadStatus: StateStatus.ERROR,
        messagesLoadError: action.payload,
      };
    }
    case ActionTypes.MESSAGES_PAGINATION_LOAD_PROCESSING: {
      return {
        ...state,
        messagesLoadStatus: StateStatus.LOADING,
      };
    }
    
    case ActionTypes.MESSAGES_COUNT_CLEAN: {
      return {
        ...state,
        messagesCount: null,
      };
    }
    case ActionTypes.MESSAGES_COUNT_LOAD_COMPLETE: {
      return {
        ...state,
        messagesCountLoadStatus: StateStatus.COMPLETE,
        messagesCount: action.payload,
        messagesCountLoadError: null,
      };
    }
    case ActionTypes.MESSAGES_COUNT_LOAD_ERROR: {
      return {
        ...state,
        messagesCountLoadStatus: StateStatus.ERROR,
        messagesCountLoadError: action.payload,
      };
    }
    case ActionTypes.MESSAGES_COUNT_LOAD_PROCESSING: {
      return {
        ...state,
        messagesCountLoadStatus: StateStatus.LOADING,
      };
    }        
    case ActionTypes.MESSAGE_SEND_COMPLETE: {
      let messageData: Message[] = Array.isArray(state.messages)
        ? state.messages
        : [];

        messageData = messageData.map((message) => {
        if (message.id === action.payload) {
          return { ...message, status: "started" };
        } else {
          return message;
        }
      });

      return {
        ...state,
        messageSendStatus: StateStatus.COMPLETE,
        messageSendError: null,
        messages: messageData,
      };
    }
    case ActionTypes.MESSAGE_SEND_ERROR: {
      return {
        ...state,
        messageSendStatus: StateStatus.ERROR,
        messageSendError: action.payload,
      };
    }
    case ActionTypes.MESSAGE_SEND_PROCESSING: {
      return {
        ...state,
        messageSendStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SECTIONS_RESTORE_STATES: {
      return {
        ...state,
        externalSchoolIntegrationSectionsLoadStatus: StateStatus.INIT,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SECTIONS_LOAD_COMPLETE: {
      return {
        ...state,
        externalSchoolIntegrationSections: action.payload,
        externalSchoolIntegrationSectionsLoadStatus: StateStatus.COMPLETE,
        externalSchoolIntegrationSectionsLoadError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SECTIONS_LOAD_ERROR: {
      return {
        ...state,
        externalSchoolIntegrationSectionsLoadStatus: StateStatus.ERROR,
        externalSchoolIntegrationSectionsLoadError: action.payload,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SECTIONS_LOAD_PROCESSING: {
      return {
        ...state,
        externalSchoolIntegrationSectionsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_GRADES_LOAD_COMPLETE: {
      return {
        ...state,
        externalSchoolIntegrationGrades: action.payload,
        externalSchoolIntegrationGradesLoadStatus: StateStatus.COMPLETE,
        externalSchoolIntegrationGradesLoadError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_GRADES_LOAD_ERROR: {
      return {
        ...state,
        externalSchoolIntegrationGrades: null,
        externalSchoolIntegrationGradesLoadStatus: StateStatus.ERROR,
        externalSchoolIntegrationGradesLoadError: action.payload,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_GRADES_LOAD_PROCESSING: {
      return {
        ...state,
        externalSchoolIntegrationGradesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTSIGNATORIES_LOAD_COMPLETE: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatories: action.payload,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.COMPLETE,
        externalSchoolIntegrationStudentSignatoriesError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTSIGNATORIES_LOAD_ERROR: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatories: null,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.ERROR,
        externalSchoolIntegrationStudentSignatoriesError: action.payload,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTSIGNATORIES_LOAD_PROCESSING: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTS_LOAD_COMPLETE: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatories: action.payload,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.COMPLETE,
        externalSchoolIntegrationStudentSignatoriesError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTS_LOAD_ERROR: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatories: null,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.ERROR,
        externalSchoolIntegrationStudentSignatoriesError: action.payload,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_STUDENTS_LOAD_PROCESSING: {
      return {
        ...state,
        externalSchoolIntegrationStudentSignatoriesLoadStatus:
          StateStatus.LOADING,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SIGNATORIES_LOAD_COMPLETE: {
      return {
        ...state,
        externalSchoolIntegrationSignatories: action.payload,
        externalSchoolIntegrationSignatoriesLoadStatus: StateStatus.COMPLETE,
        externalSchoolIntegrationSignatoriesError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SIGNATORIES_LOAD_ERROR: {
      return {
        ...state,
        externalSchoolIntegrationSignatories: null,
        externalSchoolIntegrationSignatoriesLoadStatus: StateStatus.ERROR,
        externalSchoolIntegrationSignatoriesError: null,
      };
    }
    case ActionTypes.EXTERNALSCHOOLINTEGRATION_SIGNATORIES_LOAD_PROCESSING: {
      return {
        ...state,
        externalSchoolIntegrationSignatoriesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAING_GET_SIGNINGDOCUMENTS_COMPLETE: {
      return {
        ...state,
        signingDocuments: action.payload,
        signingDocumentsLoadError: null,
        signingDocumentsLoadStatus: StateStatus.COMPLETE,
      };
    }
    case ActionTypes.CAMPAING_GET_SIGNINGDOCUMENTS_ERROR: {
      return {
        ...state,
        signingDocumentsLoadError: action.payload,
        signingDocumentsLoadStatus: StateStatus.ERROR,
      };
    }
    case ActionTypes.CAMPAING_GET_SIGNINGDOCUMENTS_PROCESSING: {
      return {
        ...state,
        signingDocumentsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_EMAIL_TRACKING_PROCESSING: {
      return {
        ...state,
        emailTrackingLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CAMPAIGN_EMAIL_TRACKING_COMPLETE: {
      return {
        ...state,
        emailTracking: action.payload.tracking || {},
        emailTrackingStats: action.payload.stats || {},
        emailTrackingLoadError: null,
        emailTrackingLoadStatus: StateStatus.COMPLETE,
      };
    }
    case ActionTypes.CAMPAIGN_EMAIL_TRACKING_ERROR: {
      return {
        ...state,
        emailTrackingLoadError: action.payload,
        emailTrackingLoadStatus: StateStatus.ERROR,
      };
    }
    case ActionTypes.CHART_CAMPAIGNS_PROCESSING: {
      return {
        ...state,
        chartDataStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.CHART_CAMPAIGNS_COMPLETE: {
      return {
        ...state,
        chartDataStatus: StateStatus.COMPLETE,
        chartData: {
          ...state.chartData,
          campaigns: action.payload,
        },
      };
    }
    case ActionTypes.CHART_CAMPAIGNS_ERROR: {
      return {
        ...state,
        chartData: undefined,
        chartDataStatus: StateStatus.ERROR,
      };
    }
    case ActionTypes.POSTGIS_CLEAN_VALUES: {
      return {
        ...state,
        postgisSchools: [],
        postgisGrades: [],
        postgisPrograms: [],
      };
    }
    case ActionTypes.POSTGIS_SCHOOLS_LOAD_COMPLETE: {
      return {
        ...state,
        postgisSchoolsLoadStatus: StateStatus.COMPLETE,
        postgisSchools: action.payload,
        postgisSchoolsLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_SCHOOLS_LOAD_ERROR: {
      return {
        ...state,
        postgisSchoolsLoadStatus: StateStatus.ERROR,
        postgisSchoolsLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_SCHOOLS_LOAD_PROCESSING: {
      return {
        ...state,
        postgisSchoolsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.POSTGIS_GRADES_LOAD_COMPLETE: {
      return {
        ...state,
        postgisGradesLoadStatus: StateStatus.COMPLETE,
        postgisGrades: action.payload,
        postgisGradesLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_GRADES_LOAD_ERROR: {
      return {
        ...state,
        postgisGradesLoadStatus: StateStatus.ERROR,
        postgisGradesLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_GRADES_LOAD_PROCESSING: {
      return {
        ...state,
        postgisGradesLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.POSTGIS_PROGRAMS_LOAD_COMPLETE: {
      return {
        ...state,
        postgisProgramsLoadStatus: StateStatus.COMPLETE,
        postgisPrograms: action.payload,
        postgisProgramsLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_PROGRAMS_LOAD_ERROR: {
      return {
        ...state,
        postgisProgramsLoadStatus: StateStatus.ERROR,
        postgisProgramsLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_PROGRAMS_LOAD_PROCESSING: {
      return {
        ...state,
        postgisProgramsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.POSTGIS_ASSIGNMENTS_LOAD_COMPLETE: {
      return {
        ...state,
        postgisAssignmentsLoadStatus: StateStatus.COMPLETE,
        postgisAssignments: action.payload,
        postgisAssignmentsLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_ASSIGNMENTS_LOAD_ERROR: {
      return {
        ...state,
        postgisAssignmentsLoadStatus: StateStatus.ERROR,
        postgisAssignmentsLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_ASSIGNMENTS_LOAD_PROCESSING: {
      return {
        ...state,
        postgisAssignmentsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_CLEAN_VALUE: {
      return {
        ...state,
        postgisStudentsLoadStatus: StateStatus.INIT,
        postgisStudents: [],
        postgisStudentsLoadError: null,
        postgisStudentsExcludedCount: 0,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_LOAD_COMPLETE: {
      return {
        ...state,
        postgisStudentsLoadStatus: StateStatus.COMPLETE,
        postgisStudents: action.payload.students,
        postgisStudentsExcludedCount: action.payload.excludedCount,
        postgisStudentsLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_LOAD_ERROR: {
      return {
        ...state,
        postgisStudentsLoadStatus: StateStatus.ERROR,
        postgisStudentsLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_LOAD_PROCESSING: {
      return {
        ...state,
        postgisStudentsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_NUMBER_LOAD_COMPLETE: {
      return {
        ...state,
        studentsForGradesAndProgramsLoadStatus: StateStatus.COMPLETE,
        studentsForGradesAndPrograms: action.payload,
        studentsForGradesAndProgramsLoadError: null,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_NUMBER_LOAD_ERROR: {
      return {
        ...state,
        studentsForGradesAndProgramsLoadStatus: StateStatus.ERROR,
        studentsForGradesAndProgramsLoadError: action.payload,
      };
    }
    case ActionTypes.POSTGIS_STUDENTS_NUMBER_LOAD_PROCESSING: {
      return {
        ...state,
        studentsForGradesAndProgramsLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.STUDENTS_BY_SCHOOL_LOAD_COMPLETE: {
      return {
        ...state,
        studentsBySchoolLoadStatus: StateStatus.COMPLETE,
        studentsBySchool: { ...state.studentsBySchool, ...action.payload },
        studentsBySchoolLoadError: null,
      };
    }
    case ActionTypes.STUDENTS_BY_SCHOOL_LOAD_ERROR: {
      return {
        ...state,
        studentsBySchoolLoadStatus: StateStatus.ERROR,
        studentsBySchoolLoadError: action.payload,
      };
    }
    case ActionTypes.STUDENTS_BY_SCHOOL_LOAD_PROCESSING: {
      return {
        ...state,
        studentsBySchoolLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.PROGRAM_DEMAND_LOAD_PROCESSING: {
      return {
        ...state,
        programDemandLoadStatus: StateStatus.LOADING,
      };
    }
    case ActionTypes.PROGRAM_DEMAND_LOAD_COMPLETE: {
      return {
        ...state,
        programDemandLoadStatus: StateStatus.COMPLETE,
        programDemand: action.payload,
        programDemandLoadError: null,
      };
    }
    case ActionTypes.PROGRAM_DEMAND_LOAD_ERROR: {
      return {
        ...state,
        programDemandLoadStatus: StateStatus.ERROR,
        programDemandLoadError: action.payload,
      };
    }
    case ActionTypes.CHOICE_DASHBOARD_LOAD_PROCESSING: {
      return {
        ...state,
        choiceDashboardLoadStatus: StateStatus.LOADING,
        choiceDashboardRequestId: (action as any).requestId ?? state.choiceDashboardRequestId,
      };
    }
    case ActionTypes.CHOICE_DASHBOARD_LOAD_COMPLETE: {
      // A response for an older request than the latest one issued is dropped: the user has
      // already moved on to other filters and the newer response (or its spinner) is on screen.
      const requestId = (action as any).requestId;
      // Dropped: a response for an older request, or any response arriving after a reset and before
      // this visit's first request (the previous visit's latest request shares the current id).
      if ((requestId !== undefined && requestId < state.choiceDashboardRequestId) || state.choiceDashboardLoadStatus === StateStatus.INIT) {
        return state;
      }
      return {
        ...state,
        choiceDashboardLoadStatus: StateStatus.COMPLETE,
        choiceDashboard: action.payload,
        choiceDashboardLoadError: null,
      };
    }
    case ActionTypes.CHOICE_DASHBOARD_RESET: {
      // Back to INIT: COMPLETE/ERROR are ignored until this visit issues its first request (PROCESSING).
      return {
        ...state,
        choiceDashboardLoadStatus: StateStatus.INIT,
        choiceDashboard: null,
        choiceDashboardLoadError: null,
      };
    }
    case ActionTypes.CHOICE_DASHBOARD_LOAD_ERROR: {
      const requestId = (action as any).requestId;
      if ((requestId !== undefined && requestId < state.choiceDashboardRequestId) || state.choiceDashboardLoadStatus === StateStatus.INIT) {
        return state;
      }
      return {
        ...state,
        choiceDashboardLoadStatus: StateStatus.ERROR,
        choiceDashboardLoadError: action.payload,
      };
    }
    case ActionTypes.PROJECTED_ENROLLMENT_LOAD_COMPLETE: {
      return {
        ...state,
        projectedEnrollmentLoadStatus: StateStatus.COMPLETE,
        projectedEnrollment: action.payload,
        projectedEnrollmentLoadError: null,
      };
    }
    case ActionTypes.PROJECTED_ENROLLMENT_LOAD_ERROR: {
      return {
        ...state,
        projectedEnrollmentLoadStatus: StateStatus.ERROR,
        projectedEnrollmentLoadError: action.payload,
      };
    }
    case ActionTypes.PROJECTED_ENROLLMENT_LOAD_PROCESSING: {
      return {
        ...state,
        projectedEnrollmentLoadStatus: StateStatus.LOADING,
      };
    }
    default:
      return state;
  }
};
